import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { marked } from 'marked';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { Drawer } from 'primeng/drawer';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { Subject, catchError, debounceTime, distinctUntilChanged, finalize, of } from 'rxjs';

import { GuiaTipoCodigo, IGuiaRecurso, IGuiaTema } from 'src/app/shared/interfaces/IGuiaUsuario';
import { CoreService } from 'src/app/shared/services/core.service';
import { GuiaPanelStateService } from 'src/app/shared/services/guia-panel-state.service';
import { GuiaUsuarioService } from 'src/app/shared/services/guia-usuario.service';

/** Debounce del buscador (ms). */
const SEARCH_DEBOUNCE_MS = 400;

/** Extensiones que ya vienen en HTML y no hay que convertir desde Markdown. */
const EXTENSIONES_HTML = ['.html', '.htm'];

/** Ícono de respaldo por tipo, para cuando el catálogo no trae uno. */
const ICONO_POR_TIPO: Record<GuiaTipoCodigo, string> = {
    TEXTO: 'pi pi-file-edit',
    PDF: 'pi pi-file-pdf',
    VIDEO: 'pi pi-video'
};

/** Estado de carga del texto de un tema. Se guarda por id de recurso. */
interface IEstadoContenido {
    cargando: boolean;
    html: string | null;
    error: string | null;
}

/**
 * Panel lateral derecho de la Guía de usuario (modal, con overlay).
 * Se abre desde el botón de ayuda del header y lista los temas de la guía del portal
 * activo, cada uno como un acordeón: al expandirlo se carga y muestra su texto, y al
 * final quedan los botones del PDF y del video del mismo tema.
 */
@Component({
    selector: 'app-guia-usuario-drawer',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        Drawer,
        AccordionModule,
        ButtonModule,
        InputTextModule,
        IconFieldModule,
        InputIconModule,
        ProgressSpinnerModule,
        TooltipModule
    ],
    templateUrl: './guia-usuario-drawer.component.html',
    styleUrls: ['./guia-usuario-drawer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GuiaUsuarioDrawerComponent {
    private readonly panelState = inject(GuiaPanelStateService);
    private readonly guiaService = inject(GuiaUsuarioService);
    private readonly core = inject(CoreService);
    private readonly destroyRef = inject(DestroyRef);

    readonly visible = this.panelState.visible;
    readonly busqueda = this.panelState.busqueda;

    /** Portal activo. Sin él no hay a qué carpeta de la guía apuntar. */
    private readonly idPortal = this.core.getIdPortalSignal();

    readonly temas = signal<IGuiaTema[]>([]);
    readonly cargando = signal(false);
    readonly error = signal<string | null>(null);

    /** Tema abierto en el acordeón. Uno a la vez: el panel mide 420 px. */
    readonly temaAbierto = signal<string>('');

    /** Texto ya cargado (o su error), indexado por id del recurso de texto. */
    readonly contenidos = signal<Record<number, IEstadoContenido>>({});

    /** No hay resultados y no es porque esté cargando o haya fallado. */
    readonly sinResultados = computed(() => !this.cargando() && !this.error() && this.temas().length === 0);

    private readonly busqueda$ = new Subject<string>();

    constructor() {
        // El buscador consulta al servidor: se espera a que el usuario deje de teclear.
        this.busqueda$
            .pipe(debounceTime(SEARCH_DEBOUNCE_MS), distinctUntilChanged(), takeUntilDestroyed())
            .subscribe(() => this.cargar());

        // Se recarga al abrir el panel y si el usuario cambia de portal con él abierto.
        // untracked: cargar() lee señales que no deben volver a disparar este efecto.
        effect(() => {
            const abierto = this.visible();
            const portal = this.idPortal();
            if (!abierto) return;

            untracked(() => {
                if (!portal) {
                    this.temas.set([]);
                    this.error.set('No se pudo identificar el portal actual.');
                    return;
                }
                this.cargar();
            });
        });
    }

    onVisibleChange(visible: boolean): void {
        this.panelState.visible.set(visible);
    }

    onBusquedaChange(valor: string): void {
        this.panelState.busqueda.set(valor);
        this.busqueda$.next(valor);
    }

    cerrar(): void {
        this.panelState.cerrar();
    }

    /** Al expandir un tema se pide su texto; ya cargado, no se vuelve a pedir. */
    onTemaAbiertoChange(valor: unknown): void {
        const tema = typeof valor === 'string' ? valor : '';
        this.temaAbierto.set(tema);
        if (!tema) return;

        const texto = this.temas().find((t) => t.tema === tema)?.texto;
        if (texto) this.cargarContenido(texto);
    }

    /** Estado del texto de un tema, para pintar spinner, error o contenido. */
    estadoContenido(recurso: IGuiaRecurso | null): IEstadoContenido | null {
        if (!recurso) return null;
        return this.contenidos()[recurso.id] ?? null;
    }

    /** Ícono a mostrar: el del catálogo si viene, si no el de respaldo del tipo. */
    iconoDe(recurso: IGuiaRecurso): string {
        return recurso.icono || ICONO_POR_TIPO[recurso.tipoCodigo] || 'pi pi-file';
    }

    /** Abre el archivo en una pestaña nueva. */
    abrir(recurso: IGuiaRecurso): void {
        const url = this.guiaService.urlAbsoluta(recurso.url);
        if (!url) return;

        window.open(url, '_blank', 'noopener');
    }

    /** Reintento manual tras un error de carga del listado. */
    reintentar(): void {
        this.cargar();
    }

    /** Reintento manual del texto de un tema. */
    reintentarContenido(recurso: IGuiaRecurso): void {
        this.contenidos.update((mapa) => {
            const { [recurso.id]: _descartado, ...resto } = mapa;
            return resto;
        });
        this.cargarContenido(recurso);
    }

    private cargar(): void {
        if (!this.visible()) return;

        const idPortal = Number(this.idPortal());
        if (!idPortal) return;

        this.cargando.set(true);
        this.error.set(null);

        this.guiaService
            .getGuias(idPortal, this.busqueda())
            .pipe(
                catchError(() => {
                    this.error.set('No se pudieron cargar los recursos de la guía.');
                    return of(null);
                }),
                finalize(() => this.cargando.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((respuesta) => {
                const temas = respuesta?.data ?? [];
                this.temas.set(temas);

                // Si el tema que estaba abierto ya no está en el resultado (cambió la
                // búsqueda o el portal), el acordeón se colapsa en vez de quedar colgado.
                const abierto = this.temaAbierto();
                if (abierto && !temas.some((t) => t.tema === abierto)) this.temaAbierto.set('');
            });
    }

    private cargarContenido(recurso: IGuiaRecurso): void {
        if (this.contenidos()[recurso.id]) return;

        this.actualizarContenido(recurso.id, { cargando: true, html: null, error: null });

        this.guiaService
            .getContenido(recurso.id)
            .pipe(
                catchError(() => of(null)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((respuesta) => {
                const datos = respuesta?.data;
                if (!datos) {
                    this.actualizarContenido(recurso.id, {
                        cargando: false,
                        html: null,
                        error: 'No se pudo cargar el texto de esta guía.'
                    });
                    return;
                }

                this.actualizarContenido(recurso.id, {
                    cargando: false,
                    html: this.aHtml(datos.contenido, datos.extension),
                    error: null
                });
            });
    }

    /**
     * Markdown se convierte con `marked`; el HTML llega listo. En ambos casos Angular
     * sanitiza al pintarlo con [innerHTML].
     */
    private aHtml(contenido: string, extension: string): string {
        if (!contenido?.trim()) return '';
        if (EXTENSIONES_HTML.includes(extension?.toLowerCase())) return contenido;

        return marked.parse(contenido, { async: false }) as string;
    }

    private actualizarContenido(id: number, estado: IEstadoContenido): void {
        this.contenidos.update((mapa) => ({ ...mapa, [id]: estado }));
    }
}
